// SPDX-License-Identifier: GPL-3.0-or-later
// The touch controls of the game page (THEME.md section 11.2). One call builds the controls in the
// playfield and binds them:
//   - the left half of the playfield holds the left flipper, the right half holds the right flipper;
//   - the launch button charges the plunger while it is held and launches on release;
//   - the nudge button nudges; the start button starts a game when the game is idle.
// Every control has a text label, so no control depends on colour. The controls send the same input
// events as the keyboard. The module also blocks scroll and zoom on the game surface, gives short
// haptic pulses when the player allows them, and lets the page unlock the audio at the first gesture.
// No DOM global is read at import time, so a test runs the module with plain objects.

import type { InputEvent } from "../src/shared/contracts.ts";
import { createTouchInput, type PointerTarget } from "./touch.ts";
import {
  blockGestures,
  createGestureLatch,
  listenForGestures,
  type SurfaceTarget,
} from "./touch-surface.ts";

/** The part of the settings store that this module reads. The `haptics` field comes from the settings. */
export interface TouchSettingsLike {
  get(): { readonly haptics?: boolean };
}

/** An element that the controls build. A real element fits. */
export interface TouchNode extends PointerTarget {
  className: string;
  textContent: string | null;
  hidden: boolean;
  setAttribute(name: string, value: string): void;
  append(...nodes: unknown[]): void;
  remove?(): void;
}

/** The element that holds the controls (the playfield). */
export interface TouchContainer {
  readonly ownerDocument: { createElement(tag: string): TouchNode };
  append(...nodes: unknown[]): void;
}

/** The game surface: it takes the pointer events of the table and the gesture block. */
export type TouchSurface = PointerTarget & SurfaceTarget;

/** The kinds of haptic pulse. */
export type HapticKind = "flipper" | "launch" | "nudge" | "big";

/** The vibration pattern of each kind, in ms. A pulse is short. */
export const HAPTIC_PATTERNS: Readonly<Record<HapticKind, number | readonly number[]>> = {
  flipper: 12,
  launch: 25,
  nudge: 35,
  big: [30, 40, 30],
};

/** Shortest time between two pulses, in ms. A faster pulse is dropped. */
export const MIN_PULSE_GAP_MS = 20;

/** The labels of the controls. */
export const TOUCH_LABELS = {
  left: "LEFT FLIPPER",
  right: "RIGHT FLIPPER",
  plunger: "HOLD TO LAUNCH",
  nudge: "NUDGE",
  start: "START GAME",
  hint: "Hold the left or right side of the table for a flipper. Hold HOLD TO LAUNCH and let go to shoot.",
} as const;

export interface TouchControlsOptions {
  readonly surface: TouchSurface;
  readonly container: TouchContainer;
  /** Send one input to the game. The same function as the keyboard uses. */
  readonly send: (ev: InputEvent) => void;
  readonly settings: TouchSettingsLike;
  /** The vibrate function. Missing: the browser one, when it exists. */
  readonly vibrate?: (pattern: number | number[]) => unknown;
  /** The clock in ms. Missing: `Date.now`. */
  readonly now?: () => number;
  /** Called when the player taps the start button. Missing: the page has no start button. */
  readonly onStart?: () => void;
}

/** A handle to the controls. */
export interface TouchControls {
  /** Lift every held control. */
  releaseAll(): void;
  /** Remove the listeners and the elements. */
  dispose(): void;
  /** Run `callback` at the first touchend or click of the player. The page unlocks audio here. */
  onFirstGesture(callback: () => void): () => void;
  /** Tell the controls that a gesture ended somewhere else (for example a click on a dialog button). */
  notifyGesture(): void;
  /** Show or hide the start button. Show it while the game is idle. */
  setIdle(idle: boolean): void;
  /** Fade the touch hints. This also happens at the first launch. */
  fadeHints(): void;
  /** Give a haptic pulse when the player allows it. The page calls it for a big event. */
  pulse(kind: HapticKind): void;
}

/** The browser vibrate function, or null. */
function browserVibrate(): ((pattern: number | number[]) => unknown) | null {
  const nav = (globalThis as { navigator?: { vibrate?: unknown } }).navigator;
  if (nav === undefined || typeof nav.vibrate !== "function") return null;
  const vibrate = nav.vibrate as (pattern: number | number[]) => unknown;
  return (pattern) => vibrate.call(nav, pattern);
}

/** A haptic player. A pulse needs the setting, the API and a gap since the last pulse. */
function createHaptics(opts: TouchControlsOptions): (kind: HapticKind) => void {
  const vibrate = opts.vibrate ?? browserVibrate();
  const now = opts.now ?? Date.now;
  let last = Number.NEGATIVE_INFINITY;
  return (kind) => {
    if (vibrate === null || opts.settings.get().haptics !== true) return;
    const time = now();
    if (time - last < MIN_PULSE_GAP_MS) return;
    last = time;
    const pattern = HAPTIC_PATTERNS[kind];
    try {
      vibrate(typeof pattern === "number" ? pattern : [...pattern]);
    } catch {
      // A browser can refuse a pulse. The game goes on.
    }
  };
}

/** The elements of the controls. */
interface Parts {
  readonly root: TouchNode;
  readonly left: TouchNode;
  readonly right: TouchNode;
  readonly plunger: TouchNode;
  readonly nudge: TouchNode;
  readonly start: TouchNode;
  readonly hint: TouchNode;
}

function make(
  container: TouchContainer,
  tag: string,
  className: string,
  label: string | null,
): TouchNode {
  const node = container.ownerDocument.createElement(tag);
  node.className = className;
  if (label !== null) node.textContent = label;
  return node;
}

/** A zone or a button: a box with a text label inside. */
function makeControl(container: TouchContainer, className: string, label: string): TouchNode {
  const box = make(container, "div", className, null);
  box.setAttribute("data-held", "false");
  box.append(make(container, "span", "touch-label", label));
  return box;
}

/** Build the elements and put them in the container. */
function buildParts(container: TouchContainer): Parts {
  const root = make(container, "div", "touch", null);
  root.setAttribute("aria-hidden", "true");
  const left = makeControl(container, "touch-zone touch-left", TOUCH_LABELS.left);
  const right = makeControl(container, "touch-zone touch-right", TOUCH_LABELS.right);
  const plunger = makeControl(container, "touch-btn touch-plunger", TOUCH_LABELS.plunger);
  const nudge = makeControl(container, "touch-btn touch-nudge", TOUCH_LABELS.nudge);
  const start = makeControl(container, "touch-btn touch-start", TOUCH_LABELS.start);
  start.hidden = true;
  const hint = make(container, "p", "touch-hint", TOUCH_LABELS.hint);
  hint.setAttribute("data-state", "shown");
  root.append(left, right, plunger, nudge, start, hint);
  container.append(root);
  return { root, left, right, plunger, nudge, start, hint };
}

/** The element that shows the hold state of an action, or null. */
function heldNode(parts: Parts, action: InputEvent["action"]): TouchNode | null {
  if (action === "left_flipper") return parts.left;
  if (action === "right_flipper") return parts.right;
  return action === "plunger" ? parts.plunger : null;
}

/** The haptic kind of an input, or null when the input has no pulse. */
function pulseKind(ev: InputEvent): HapticKind | null {
  const flipper = ev.action === "left_flipper" || ev.action === "right_flipper";
  if (flipper && ev.pressed) return "flipper";
  if (ev.action === "plunger" && !ev.pressed) return "launch";
  return ev.action === "nudge_up" && ev.pressed ? "nudge" : null;
}

/** A tap on the start button: the finger goes down and up on the button. */
function bindStart(parts: Parts, onStart: (() => void) | undefined): () => void {
  if (onStart === undefined) return () => {};
  let armed = false;
  const down = (ev: { preventDefault(): void; stopPropagation?(): void }): void => {
    ev.preventDefault();
    ev.stopPropagation?.();
    armed = true;
  };
  const up = (): void => {
    if (!armed) return;
    armed = false;
    onStart();
  };
  const cancel = (): void => {
    armed = false;
  };
  parts.start.addEventListener("pointerdown", down);
  parts.start.addEventListener("pointerup", up);
  parts.start.addEventListener("pointercancel", cancel);
  return () => {
    parts.start.removeEventListener("pointerdown", down);
    parts.start.removeEventListener("pointerup", up);
    parts.start.removeEventListener("pointercancel", cancel);
  };
}

/**
 * Build the touch controls and bind them. The surface gets the table fingers (two fingers nudge)
 * and the gesture block. The result lifts every held control and removes everything at `dispose`.
 */
export function createTouchControls(opts: TouchControlsOptions): TouchControls {
  const parts = buildParts(opts.container);
  const haptic = createHaptics(opts);
  const latch = createGestureLatch();
  const fadeHints = (): void => parts.hint.setAttribute("data-state", "faded");
  const send = (ev: InputEvent): void => {
    opts.send(ev);
    heldNode(parts, ev.action)?.setAttribute("data-held", String(ev.pressed));
    const kind = pulseKind(ev);
    if (kind !== null) haptic(kind);
    // The first launch ends the first ball: the hints have done their work.
    if (ev.action === "plunger" && !ev.pressed) fadeHints();
  };
  const input = createTouchInput(
    {
      left: parts.left,
      right: parts.right,
      plunger: parts.plunger,
      nudge: parts.nudge,
      table: opts.surface,
    },
    send,
  );
  const stops = [
    blockGestures(opts.surface),
    listenForGestures(opts.surface, latch),
    bindStart(parts, opts.onStart),
  ];
  return {
    releaseAll: () => input.releaseAll(),
    dispose() {
      input.dispose();
      for (const stop of stops) stop();
      parts.root.remove?.();
    },
    onFirstGesture: (callback) => latch.onFirst(callback),
    notifyGesture: () => latch.fire(),
    setIdle(idle) {
      parts.start.hidden = !idle || opts.onStart === undefined;
    },
    fadeHints,
    pulse: haptic,
  };
}
